import { TFunction } from "i18next";
import { jsPDF } from "jspdf";
import { ProjectBlockStatus } from "../../Services/ProjectService";
import { IProjectPdfLogo } from "./projectPdfLogo";
import { IPdfThemeColors, PdfStatusVariant, lightenRgb } from "./projectPdfTheme";

export const PDF_PAGE_WIDTH = 210;
export const PDF_MARGIN_X = 14;
export const PDF_MARGIN_TOP = 18;
export const PDF_CONTENT_WIDTH = PDF_PAGE_WIDTH - PDF_MARGIN_X * 2;
export const PDF_PAGE_BOTTOM = 272;
export const PDF_LINE_HEIGHT = 4.6;
export const PDF_SECTION_GAP = 8;
export const PDF_METRIC_GAP = 6;
export const PDF_EMPTY_VALUE = "—";
export const PDF_COL_GAP = 6;
export const PDF_CARD_PAD = 4;
export const PDF_METRIC_NEST_OFFSET = 8;
export const PDF_INNER_WIDTH = PDF_CONTENT_WIDTH - PDF_CARD_PAD * 2;
export const PDF_NESTED_CONTENT_WIDTH = PDF_CONTENT_WIDTH - PDF_METRIC_NEST_OFFSET;

export const PDF_FONT_TITLE = 17;
export const PDF_FONT_SECTION = 11.5;
export const PDF_FONT_BODY = 9.5;
export const PDF_FONT_SMALL = 8;
export const PDF_FONT_LABEL = 7.5;
const PDF_LOGO_GAP_MM = 4;
const PDF_COVER_TITLE_LINE_MM = 6.4;
const PDF_COVER_TEXT_PHASE_GAP_MM = 3;
const PDF_HALF_COL_WIDTH = (PDF_CONTENT_WIDTH - PDF_COL_GAP) / 2;
const PDF_METRIC_HALF_WIDTH = (PDF_INNER_WIDTH - PDF_COL_GAP) / 2;

export interface IPdfLabelValue {
    label: string;
    value: string;
}

export const setTextColor = (doc: jsPDF, color: [number, number, number]) => {
    doc.setTextColor(color[0], color[1], color[2]);
};

export const setFillColor = (doc: jsPDF, color: [number, number, number]) => {
    doc.setFillColor(color[0], color[1], color[2]);
};

export const setDrawColor = (doc: jsPDF, color: [number, number, number]) => {
    doc.setDrawColor(color[0], color[1], color[2]);
};

export const resolveStatusVariant = (status: ProjectBlockStatus | undefined): PdfStatusVariant => {
    switch (status) {
        case "APPROVED":
            return "approved";
        case "WAITING_DASHLY":
        case "WAITING_CUSTOMER":
            return "pending";
        default:
            return "concept";
    }
};

export const drawPageFooter = (
    doc: jsPDF,
    pageNumber: number,
    pageCount: number,
    projectName: string,
    theme: IPdfThemeColors,
) => {
    const footerY = 288;
    setDrawColor(doc, theme.border);
    doc.setLineWidth(0.3);
    doc.line(PDF_MARGIN_X, footerY - 4, PDF_PAGE_WIDTH - PDF_MARGIN_X, footerY - 4);

    doc.setFontSize(PDF_FONT_SMALL);
    doc.setFont("helvetica", "bold");
    setTextColor(doc, theme.primary);
    doc.text("Dashly", PDF_MARGIN_X, footerY);

    doc.setFont("helvetica", "normal");
    setTextColor(doc, theme.muted);
    doc.text(projectName.trim() || PDF_EMPTY_VALUE, PDF_MARGIN_X + 22, footerY);
    doc.text(`${pageNumber} / ${pageCount}`, PDF_PAGE_WIDTH - PDF_MARGIN_X, footerY, { align: "right" });
};

export const drawContinuationHeader = (doc: jsPDF, projectName: string, theme: IPdfThemeColors) => {
    setFillColor(doc, theme.surfaceStrong);
    doc.rect(0, 0, PDF_PAGE_WIDTH, 16, "F");
    doc.setFontSize(PDF_FONT_SMALL);
    doc.setFont("helvetica", "bold");
    setTextColor(doc, theme.primary);
    doc.text(projectName.trim() || PDF_EMPTY_VALUE, PDF_MARGIN_X, 10);
    setDrawColor(doc, theme.border);
    doc.setLineWidth(0.3);
    doc.line(PDF_MARGIN_X, 14.5, PDF_PAGE_WIDTH - PDF_MARGIN_X, 14.5);
    setTextColor(doc, theme.text);
};

const drawCoverLogoAt = (doc: jsPDF, logo: IProjectPdfLogo, x: number, y: number) => {
    doc.addImage(logo.dataUrl, logo.format, x, y, logo.widthMm, logo.heightMm);
};

const resolveCoverLogosBlock = (
    dashlyLogo: IProjectPdfLogo | null,
    customerLogo: IProjectPdfLogo | null,
): { widthMm: number; heightMm: number } => {
    if (!dashlyLogo && !customerLogo) {
        return { widthMm: 0, heightMm: 0 };
    }
    const heightMm = Math.max(dashlyLogo?.heightMm ?? 0, customerLogo?.heightMm ?? 0);
    const widthMm =
        (dashlyLogo?.widthMm ?? 0) +
        (dashlyLogo && customerLogo ? PDF_LOGO_GAP_MM : 0) +
        (customerLogo?.widthMm ?? 0);
    return { widthMm, heightMm };
};

const drawCoverLogos = (
    doc: jsPDF,
    dashlyLogo: IProjectPdfLogo | null,
    customerLogo: IProjectPdfLogo | null,
    rowTop: number,
    rowHeight: number,
): void => {
    if (!dashlyLogo && !customerLogo) {
        return;
    }

    let cursorX = PDF_PAGE_WIDTH - PDF_MARGIN_X;

    if (customerLogo) {
        cursorX -= customerLogo.widthMm;
        drawCoverLogoAt(
            doc,
            customerLogo,
            cursorX,
            rowTop + (rowHeight - customerLogo.heightMm) / 2,
        );
        cursorX -= dashlyLogo ? PDF_LOGO_GAP_MM : 0;
    }

    if (dashlyLogo) {
        cursorX -= dashlyLogo.widthMm;
        drawCoverLogoAt(doc, dashlyLogo, cursorX, rowTop + (rowHeight - dashlyLogo.heightMm) / 2);
    }
};

const measureTitleVisualHeight = (doc: jsPDF, titleLines: string[]): number => {
    if (titleLines.length === 0) {
        return 0;
    }
    const firstLineHeight = doc.getTextDimensions(titleLines[0]).h;
    if (titleLines.length === 1) {
        return firstLineHeight;
    }
    return firstLineHeight + (titleLines.length - 1) * PDF_COVER_TITLE_LINE_MM;
};

const resolveTitleBaselineForRowCenter = (
    doc: jsPDF,
    titleLines: string[],
    rowTop: number,
    rowHeight: number,
): number => {
    const titleVisualHeight = measureTitleVisualHeight(doc, titleLines);
    const firstLineHeight = doc.getTextDimensions(titleLines[0] ?? "").h;
    const ascender = firstLineHeight * 0.76;
    const titleVisualTop = rowTop + (rowHeight - titleVisualHeight) / 2;
    return titleVisualTop + ascender;
};

const drawMetaSummaryStrip = (
    doc: jsPDF,
    y: number,
    exportDate: string,
    metaParts: string[],
    theme: IPdfThemeColors,
): number => {
    const cells = [exportDate, ...metaParts].filter((part) => part.trim().length > 0);
    if (cells.length === 0) {
        return y;
    }

    const columns = Math.min(3, cells.length);
    const cellWidth = (PDF_CONTENT_WIDTH - PDF_COL_GAP * (columns - 1)) / columns;
    const rows = Math.ceil(cells.length / columns);
    const rowHeight = 9;
    const stripHeight = rows * rowHeight + 4;

    setFillColor(doc, theme.surface);
    setDrawColor(doc, theme.border);
    doc.setLineWidth(0.25);
    doc.roundedRect(PDF_MARGIN_X, y, PDF_CONTENT_WIDTH, stripHeight, 1.5, 1.5, "FD");

    cells.forEach((cell, index) => {
        const col = index % columns;
        const row = Math.floor(index / columns);
        const cellX = PDF_MARGIN_X + col * (cellWidth + PDF_COL_GAP);
        const cellY = y + 5.5 + row * rowHeight;
        doc.setFontSize(PDF_FONT_SMALL);
        doc.setFont("helvetica", "normal");
        setTextColor(doc, theme.text);
        const lines = doc.splitTextToSize(cell, cellWidth - 4);
        doc.text(lines[0] ?? cell, cellX + 2, cellY);
    });

    return y + stripHeight + 6;
};

export const drawCoverHeader = (
    doc: jsPDF,
    projectName: string,
    phaseLabel: string,
    exportDate: string,
    metaParts: string[],
    theme: IPdfThemeColors,
    dashlyLogo: IProjectPdfLogo | null,
    customerLogo: IProjectPdfLogo | null,
): number => {
    const headerTop = PDF_MARGIN_TOP - 2;
    const logosBlock = resolveCoverLogosBlock(dashlyLogo, customerLogo);
    const title = projectName.trim() || PDF_EMPTY_VALUE;

    doc.setFontSize(PDF_FONT_TITLE);
    doc.setFont("helvetica", "bold");
    const titleWidth = Math.max(
        PDF_CONTENT_WIDTH - logosBlock.widthMm - 8,
        PDF_CONTENT_WIDTH * 0.45,
    );
    const titleLines = doc.splitTextToSize(title, titleWidth);
    const rowHeight = Math.max(logosBlock.heightMm, 12);
    const rowTop = headerTop;

    drawCoverLogos(doc, dashlyLogo, customerLogo, rowTop, rowHeight);

    setTextColor(doc, theme.primary);
    const titleBaseline = resolveTitleBaselineForRowCenter(doc, titleLines, rowTop, rowHeight);
    doc.text(titleLines, PDF_MARGIN_X, titleBaseline);

    let y = rowTop + rowHeight + PDF_COVER_TEXT_PHASE_GAP_MM + 2;

    doc.setFontSize(PDF_FONT_SMALL);
    doc.setFont("helvetica", "bold");
    const phaseTextWidth = doc.getTextWidth(phaseLabel);
    const phasePadX = 3;
    const phaseBadgeWidth = phaseTextWidth + phasePadX * 2;
    const phaseBadgeHeight = 5.5;
    setFillColor(doc, theme.surfaceStrong);
    doc.roundedRect(PDF_MARGIN_X, y - phaseBadgeHeight + 1.5, phaseBadgeWidth, phaseBadgeHeight, 1.5, 1.5, "F");
    setTextColor(doc, theme.primary);
    doc.text(phaseLabel, PDF_MARGIN_X + phasePadX, y);

    y += 8;
    y = drawMetaSummaryStrip(doc, y, exportDate, metaParts, theme);
    return y;
};

export const ensurePageSpace = (
    doc: jsPDF,
    y: number,
    needed: number,
    projectName: string,
    theme: IPdfThemeColors,
): number => {
    if (y + needed > PDF_PAGE_BOTTOM) {
        doc.addPage();
        drawContinuationHeader(doc, projectName, theme);
        return 22;
    }
    return y;
};

export const writeWrappedLines = (
    doc: jsPDF,
    lines: string[],
    x: number,
    y: number,
    lineHeight: number,
    projectName: string,
    theme: IPdfThemeColors,
): number => {
    let currentY = y;
    lines.forEach((line) => {
        currentY = ensurePageSpace(doc, currentY, lineHeight, projectName, theme);
        doc.text(line, x, currentY);
        currentY += lineHeight;
    });
    return currentY;
};

export const drawBadge = (
    doc: jsPDF,
    x: number,
    y: number,
    text: string,
    variant: PdfStatusVariant,
    theme: IPdfThemeColors,
): number => {
    const colors = theme.statusBadges[variant];
    doc.setFontSize(PDF_FONT_SMALL);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(text);
    const padX = 2.5;
    const badgeWidth = textWidth + padX * 2;
    const badgeHeight = 5.2;

    setFillColor(doc, colors.bg);
    doc.roundedRect(x, y - badgeHeight + 1.4, badgeWidth, badgeHeight, 1.4, 1.4, "F");
    setTextColor(doc, colors.text);
    doc.text(text, x + padX, y);
    setTextColor(doc, theme.text);
    doc.setFont("helvetica", "normal");
    return badgeWidth;
};

export const drawApprovalChips = (
    doc: jsPDF,
    x: number,
    y: number,
    customerApproved: boolean | undefined,
    dashlyApproved: boolean | undefined,
    t: TFunction,
    theme: IPdfThemeColors,
): number => {
    doc.setFontSize(PDF_FONT_SMALL);
    let cursorX = x;

    const drawChip = (label: string, approved: boolean) => {
        const value = approved ? t("common.yes") : t("common.no");
        const chipText = `${label}: ${value}`;
        const textWidth = doc.getTextWidth(chipText);
        const padX = 2.2;
        const chipWidth = textWidth + padX * 2;
        const chipHeight = 5;

        setFillColor(doc, approved ? mixSoft(theme.good) : theme.surface);
        doc.roundedRect(cursorX, y - chipHeight + 1.3, chipWidth, chipHeight, 1.2, 1.2, "F");
        doc.setFont("helvetica", approved ? "bold" : "normal");
        setTextColor(doc, approved ? theme.good : theme.muted);
        doc.text(chipText, cursorX + padX, y);
        cursorX += chipWidth + 3;
    };

    drawChip(t("project.approval.customer"), Boolean(customerApproved));
    drawChip(t("project.approval.dashly"), Boolean(dashlyApproved));
    setTextColor(doc, theme.text);
    doc.setFont("helvetica", "normal");
    return y;
};

const mixSoft = (color: [number, number, number]): [number, number, number] => [
    Math.round(color[0] * 0.14 + 255 * 0.86),
    Math.round(color[1] * 0.14 + 255 * 0.86),
    Math.round(color[2] * 0.14 + 255 * 0.86),
];

export const drawStatusApprovalRow = (
    doc: jsPDF,
    x: number,
    y: number,
    statusLabel: string,
    statusVariant: PdfStatusVariant,
    customerApproved: boolean | undefined,
    dashlyApproved: boolean | undefined,
    t: TFunction,
    theme: IPdfThemeColors,
): number => {
    const badgeWidth = drawBadge(doc, x, y, statusLabel, statusVariant, theme);
    drawApprovalChips(doc, x + badgeWidth + 4, y, customerApproved, dashlyApproved, t, theme);
    // Chips extend ~4mm above the baseline; leave clear space before next content.
    return y + 9;
};

const measureLabelValueHeight = (
    doc: jsPDF,
    label: string,
    value: string,
    contentWidth: number,
): number => {
    const text = value.trim() || PDF_EMPTY_VALUE;
    doc.setFontSize(PDF_FONT_LABEL);
    doc.setFont("helvetica", "bold");
    const labelLines = doc.splitTextToSize(label.toUpperCase(), contentWidth);
    doc.setFontSize(PDF_FONT_BODY);
    doc.setFont("helvetica", "normal");
    const valueLines = doc.splitTextToSize(text, contentWidth);
    return labelLines.length * (PDF_LINE_HEIGHT - 0.6) + valueLines.length * PDF_LINE_HEIGHT + 2;
};

const writeLabelValueAt = (
    doc: jsPDF,
    label: string,
    value: string,
    x: number,
    y: number,
    contentWidth: number,
    projectName: string,
    theme: IPdfThemeColors,
): number => {
    const text = value.trim() || PDF_EMPTY_VALUE;
    let currentY = ensurePageSpace(doc, y, PDF_LINE_HEIGHT * 2, projectName, theme);

    doc.setFontSize(PDF_FONT_LABEL);
    doc.setFont("helvetica", "bold");
    setTextColor(doc, theme.muted);
    const labelLines = doc.splitTextToSize(label.toUpperCase(), contentWidth);
    currentY = writeWrappedLines(
        doc,
        labelLines,
        x,
        currentY,
        PDF_LINE_HEIGHT - 0.6,
        projectName,
        theme,
    );

    doc.setFontSize(PDF_FONT_BODY);
    doc.setFont("helvetica", "normal");
    setTextColor(doc, theme.text);
    const valueLines = doc.splitTextToSize(text, contentWidth);
    currentY = writeWrappedLines(doc, valueLines, x, currentY, PDF_LINE_HEIGHT, projectName, theme);
    return currentY + 2;
};

export const writeLabelValue = (
    doc: jsPDF,
    label: string,
    value: string,
    y: number,
    projectName: string,
    theme: IPdfThemeColors,
    indent = PDF_MARGIN_X,
    contentWidth = PDF_CONTENT_WIDTH,
): number => writeLabelValueAt(doc, label, value, indent, y, contentWidth, projectName, theme);

export const writeTwoColumnFields = (
    doc: jsPDF,
    left: IPdfLabelValue,
    right: IPdfLabelValue | null,
    y: number,
    projectName: string,
    theme: IPdfThemeColors,
    indent = PDF_MARGIN_X,
    totalWidth = PDF_CONTENT_WIDTH,
): number => {
    if (!right) {
        return writeLabelValue(doc, left.label, left.value, y, projectName, theme, indent, totalWidth);
    }

    const colWidth = (totalWidth - PDF_COL_GAP) / 2;
    const leftHeight = measureLabelValueHeight(doc, left.label, left.value, colWidth);
    const rightHeight = measureLabelValueHeight(doc, right.label, right.value, colWidth);
    const needed = Math.max(leftHeight, rightHeight) + 1;
    const startY = ensurePageSpace(doc, y, Math.min(needed, 24), projectName, theme);

    const leftEnd = writeLabelValueAt(
        doc,
        left.label,
        left.value,
        indent,
        startY,
        colWidth,
        projectName,
        theme,
    );
    const rightEnd = writeLabelValueAt(
        doc,
        right.label,
        right.value,
        indent + colWidth + PDF_COL_GAP,
        startY,
        colWidth,
        projectName,
        theme,
    );
    return Math.max(leftEnd, rightEnd) + 1;
};

export const writeFieldGrid = (
    doc: jsPDF,
    fields: IPdfLabelValue[],
    y: number,
    projectName: string,
    theme: IPdfThemeColors,
    indent = PDF_MARGIN_X,
    totalWidth = PDF_CONTENT_WIDTH,
): number => {
    const nonEmpty = fields.filter((field) => field.label.trim().length > 0);
    let currentY = y;
    for (let index = 0; index < nonEmpty.length; index += 2) {
        const left = nonEmpty[index];
        const right = nonEmpty[index + 1] ?? null;
        currentY = writeTwoColumnFields(doc, left, right, currentY, projectName, theme, indent, totalWidth);
    }
    return currentY;
};

export const drawSubsectionTitle = (
    doc: jsPDF,
    title: string,
    y: number,
    projectName: string,
    theme: IPdfThemeColors,
    indent = PDF_MARGIN_X,
): number => {
    let currentY = ensurePageSpace(doc, y, PDF_LINE_HEIGHT + 3, projectName, theme);
    doc.setFontSize(PDF_FONT_LABEL);
    doc.setFont("helvetica", "bold");
    setTextColor(doc, theme.primary);
    doc.text(title.toUpperCase(), indent, currentY);
    currentY += 2;
    setDrawColor(doc, theme.border);
    doc.setLineWidth(0.25);
    doc.line(indent, currentY, indent + Math.min(40, PDF_CONTENT_WIDTH * 0.35), currentY);
    return currentY + 4;
};

export const startDashboardPage = (
    doc: jsPDF,
    projectName: string,
    theme: IPdfThemeColors,
): number => {
    doc.addPage();
    drawContinuationHeader(doc, projectName, theme);
    return 22;
};

export const formatDashboardPdfTitle = (
    t: TFunction,
    dashboardIndex: number,
    dashboardName: string,
): string => {
    const label = t("project.export.dashboardLabel", {
        number: dashboardIndex + 1,
        defaultValue: "Dashboard {{number}}",
    });
    const name = dashboardName.trim() || t("project.dashboard.defaultName", { defaultValue: "Nieuw dashboard" });
    return `${label} — ${name}`;
};

export { sortMetricsByEntryOrder } from "./projectMetricUtils";

export const formatMetricPdfTitle = (
    t: TFunction,
    metricIndex: number,
    metricTitle: string,
): string => {
    const label = t("project.export.metricLabel", {
        number: metricIndex + 1,
        defaultValue: "Metric {{number}}",
    });
    const name = metricTitle.trim() || t("project.metric.defaultTitle", { defaultValue: "Nieuwe metric" });
    return `${label} — ${name}`;
};

export const drawDashboardTitle = (
    doc: jsPDF,
    title: string,
    y: number,
    projectName: string,
    theme: IPdfThemeColors,
): number => {
    let currentY = ensurePageSpace(doc, y, 14, projectName, theme);
    const titleLines = (() => {
        doc.setFontSize(PDF_FONT_SECTION);
        doc.setFont("helvetica", "bold");
        return doc.splitTextToSize(title, PDF_CONTENT_WIDTH - 12);
    })();
    const blockHeight = titleLines.length * (PDF_LINE_HEIGHT + 0.5) + 7;
    const headerBg = lightenRgb(theme.primary, 0.86);
    const headerBorder = lightenRgb(theme.primary, 0.55);

    setFillColor(doc, headerBg);
    setDrawColor(doc, headerBorder);
    doc.setLineWidth(0.35);
    doc.roundedRect(PDF_MARGIN_X, currentY - 3, PDF_CONTENT_WIDTH, blockHeight, 2, 2, "FD");

    setTextColor(doc, theme.primary);
    currentY = writeWrappedLines(
        doc,
        titleLines,
        PDF_MARGIN_X + 6,
        currentY + 3,
        PDF_LINE_HEIGHT + 0.5,
        projectName,
        theme,
    );
    setTextColor(doc, theme.text);
    return currentY + 6;
};

export const drawMetricCardStart = (
    doc: jsPDF,
    title: string,
    y: number,
    projectName: string,
    theme: IPdfThemeColors,
    indent = PDF_MARGIN_X + PDF_METRIC_NEST_OFFSET,
    contentWidth = PDF_NESTED_CONTENT_WIDTH,
): { y: number; contentX: number; contentWidth: number } => {
    const startY = ensurePageSpace(doc, y, 16, projectName, theme);
    let currentY = startY;
    doc.setFontSize(PDF_FONT_BODY);
    doc.setFont("helvetica", "bold");
    const titleLines = doc.splitTextToSize(title, contentWidth - PDF_CARD_PAD * 2 - 4);
    const headerHeight = titleLines.length * PDF_LINE_HEIGHT + 5;
    // Same palette as UI approval pill, driven by the customer's brand secondary color.
    const headerBg = lightenRgb(theme.secondary, 0.92);
    const headerBorder = lightenRgb(theme.secondary, 0.6);
    const headerText = theme.secondary;

    setFillColor(doc, headerBg);
    setDrawColor(doc, headerBorder);
    doc.setLineWidth(0.3);
    doc.roundedRect(indent, currentY - 2, contentWidth, headerHeight, 1.4, 1.4, "FD");

    setTextColor(doc, headerText);
    currentY = writeWrappedLines(
        doc,
        titleLines,
        indent + PDF_CARD_PAD + 2,
        currentY + 2,
        PDF_LINE_HEIGHT,
        projectName,
        theme,
    );
    setTextColor(doc, theme.text);

    const boxBottom = startY - 2 + headerHeight;
    return {
        y: Math.max(currentY + 2, boxBottom + 5),
        contentX: indent + PDF_CARD_PAD,
        contentWidth: contentWidth - PDF_CARD_PAD * 2,
    };
};

export const writeBulletList = (
    doc: jsPDF,
    label: string,
    values: string[],
    y: number,
    indent: number,
    projectName: string,
    contentWidth: number,
    theme: IPdfThemeColors,
): number => {
    const nonEmptyValues = values.map((value) => value.trim()).filter(Boolean);
    if (nonEmptyValues.length === 0) {
        return y;
    }

    let currentY = ensurePageSpace(doc, y, PDF_LINE_HEIGHT, projectName, theme);
    doc.setFontSize(PDF_FONT_LABEL);
    doc.setFont("helvetica", "bold");
    setTextColor(doc, theme.muted);
    doc.text(label.toUpperCase(), indent, currentY);
    currentY += PDF_LINE_HEIGHT - 0.4;

    doc.setFontSize(PDF_FONT_BODY);
    doc.setFont("helvetica", "normal");
    setTextColor(doc, theme.text);
    nonEmptyValues.forEach((value) => {
        const lines = doc.splitTextToSize(`•  ${value}`, contentWidth);
        currentY = writeWrappedLines(doc, lines, indent, currentY, PDF_LINE_HEIGHT, projectName, theme);
    });

    return currentY + 2;
};

export const writeTwoColumnBulletLists = (
    doc: jsPDF,
    left: { label: string; values: string[] },
    right: { label: string; values: string[] },
    y: number,
    indent: number,
    totalWidth: number,
    projectName: string,
    theme: IPdfThemeColors,
): number => {
    const leftValues = left.values.map((value) => value.trim()).filter(Boolean);
    const rightValues = right.values.map((value) => value.trim()).filter(Boolean);
    if (leftValues.length === 0 && rightValues.length === 0) {
        return y;
    }
    if (leftValues.length === 0) {
        return writeBulletList(doc, right.label, rightValues, y, indent, projectName, totalWidth, theme);
    }
    if (rightValues.length === 0) {
        return writeBulletList(doc, left.label, leftValues, y, indent, projectName, totalWidth, theme);
    }

    const colWidth = (totalWidth - PDF_COL_GAP) / 2;
    const startY = ensurePageSpace(doc, y, PDF_LINE_HEIGHT * 3, projectName, theme);
    const leftEnd = writeBulletList(doc, left.label, leftValues, startY, indent, projectName, colWidth, theme);
    const rightEnd = writeBulletList(
        doc,
        right.label,
        rightValues,
        startY,
        indent + colWidth + PDF_COL_GAP,
        projectName,
        colWidth,
        theme,
    );
    return Math.max(leftEnd, rightEnd);
};

export const sanitizePdfFilename = (name: string): string => {
    const sanitized = name
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "_");
    return sanitized || "project";
};

const padPdfTimestampPart = (value: number): string => String(value).padStart(2, "0");

export const formatPdfFilenameTimestamp = (date = new Date()): string =>
    `${padPdfTimestampPart(date.getDate())}-${padPdfTimestampPart(date.getMonth() + 1)}-${date.getFullYear()}-${padPdfTimestampPart(date.getHours())}${padPdfTimestampPart(date.getMinutes())}`;

export const applyPdfFooters = (
    doc: jsPDF,
    projectName: string,
    theme: IPdfThemeColors,
): void => {
    const displayProjectName = projectName.trim() || PDF_EMPTY_VALUE;
    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
        doc.setPage(page);
        drawPageFooter(doc, page, pageCount, displayProjectName, theme);
    }
};

export const buildPdfFilename = (projectName: string, filenameSuffix: string, date = new Date()): string =>
    `${sanitizePdfFilename(projectName)}_${filenameSuffix}_${formatPdfFilenameTimestamp(date)}.pdf`;

export const buildFinalizedPdfBlob = (
    doc: jsPDF,
    projectName: string,
    filenameSuffix: string,
    theme: IPdfThemeColors,
): { blob: Blob; filename: string } => {
    applyPdfFooters(doc, projectName, theme);
    return {
        blob: doc.output("blob"),
        filename: buildPdfFilename(projectName, filenameSuffix),
    };
};

export const finalizePdfDocument = (
    doc: jsPDF,
    projectName: string,
    filenameSuffix: string,
    theme: IPdfThemeColors,
): void => {
    const { filename } = buildFinalizedPdfBlob(doc, projectName, filenameSuffix, theme);
    doc.save(filename);
};

export { PDF_HALF_COL_WIDTH, PDF_METRIC_HALF_WIDTH };
